import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import {
  PageHeader,
  Select,
  SubjectCell,
  bacenCasesHref,
  useCodeLabel,
  useFormats,
} from '@/pages/bacen/shared';
import { bacenCaseParams, fetchBacenCases } from '@/services/bacen';
import type { BacenCaseRow } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * Every row of the BACEN ledger, filtered: one accused in one PAS.
 *
 * Persons and companies sit in the same table because the ledger publishes
 * them together; a person is a name and a masked CPF, never a link. The
 * status axes above the table are two different processes (whether the CRSFN
 * ruled, and where collection stands) and disagree by design: a ruled row can
 * still be owed.
 *
 * Every filter seeds from the URL, because the overview, the appeals screen
 * and the company profile link here with a filter already applied.
 */
export function BacenCasesPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, currency, percent } = useFormats();
  const label = useCodeLabel();
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('query') ?? '');
  const [query, setQuery] = useState<string | null>(seed.get('query') || null);
  const [penaltyCode, setPenaltyCode] = useState(seed.get('penalty_code') ?? '');
  const [statusFamily, setStatusFamily] = useState(seed.get('status_family') ?? '');
  const [office, setOffice] = useState(seed.get('office') ?? '');
  const [year, setYear] = useState(seed.get('year') ?? '');
  const [subjectType, setSubjectType] = useState(seed.get('subject_type') ?? '');
  const [appealKind, setAppealKind] = useState(seed.get('appeal_kind') ?? '');
  const [trajectory, setTrajectory] = useState(seed.get('trajectory') ?? '');
  const [inRegistry, setInRegistry] = useState(seed.get('in_registry') ?? '');
  const [entity, setEntity] = useState(seed.get('entity') ?? '');

  const filters = {
    query,
    penalty_code: penaltyCode || null,
    status_family: statusFamily || null,
    office: office || null,
    year: year ? Number(year) : null,
    subject_type: subjectType || null,
    appeal_kind: appealKind || null,
    trajectory: trajectory || null,
    in_registry: inRegistry === '' ? null : inRegistry === 'true',
    entity: entity || null,
  };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['bacen', 'cases', filters],
    queryFn: ({ pageParam }) =>
      fetchBacenCases({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });

  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const options = data?.options ?? {};

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        page="bacen:cases"
        exportPath={`/api/v1/bacen/export/cases?${bacenCaseParams(filters).toString()}`}
      />

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="bacen-case-query" className="text-xs font-medium text-foreground">
            {t('bacen:cases.search.label')}
          </label>
          <input
            id="bacen-case-query"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('bacen:cases.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <Select
          id="bacen-penalty"
          label={t('bacen:cases.filters.penalty')}
          any={t('bacen:cases.filters.any')}
          value={penaltyCode}
          onChange={setPenaltyCode}
          options={options.penalty_code}
          render={(o) => label('penalty', o.value)}
        />
        <Select
          id="bacen-status"
          label={t('bacen:cases.filters.status')}
          any={t('bacen:cases.filters.any')}
          value={statusFamily}
          onChange={setStatusFamily}
          options={options.status_family}
          render={(o) => label('statusFamily', o.value)}
        />
        <Select
          id="bacen-office"
          label={t('bacen:cases.filters.office')}
          any={t('bacen:cases.filters.any')}
          value={office}
          onChange={setOffice}
          options={options.office}
        />
        <Select
          id="bacen-year"
          label={t('bacen:cases.filters.year')}
          any={t('bacen:cases.filters.any')}
          value={year}
          onChange={setYear}
          options={options.year}
        />
        <Select
          id="bacen-subject"
          label={t('bacen:cases.filters.subject')}
          any={t('bacen:cases.filters.any')}
          value={subjectType}
          onChange={setSubjectType}
          options={options.subject_type}
          render={(o) => label('subjectType', o.value)}
        />
        <Select
          id="bacen-appeal"
          label={t('bacen:cases.filters.appealKind')}
          any={t('bacen:cases.filters.any')}
          value={appealKind}
          onChange={setAppealKind}
          options={options.appeal_kind}
          render={(o) => label('appealKind', o.value)}
        />
        <Select
          id="bacen-trajectory"
          label={t('bacen:cases.filters.trajectory')}
          any={t('bacen:cases.filters.any')}
          value={trajectory}
          onChange={setTrajectory}
          options={options.trajectory}
          render={(o) => label('trajectory', o.value)}
        />
        <div className="space-y-1">
          <label htmlFor="bacen-registry" className="block text-xs font-medium text-foreground">
            {t('bacen:cases.filters.inRegistry')}
          </label>
          <select
            id="bacen-registry"
            value={inRegistry}
            onChange={(event) => setInRegistry(event.target.value)}
            className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('bacen:cases.filters.any')}</option>
            <option value="true">{t('bacen:cases.filters.inRegistry_yes')}</option>
            <option value="false">{t('bacen:cases.filters.inRegistry_no')}</option>
          </select>
        </div>
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('bacen:cases.search.submit')}
        </button>
      </form>

      {entity ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-entity-chip>
          <span>{t('bacen:cases.filters.entityChip', { cnpj: entity })}</span>
          <button
            type="button"
            onClick={() => setEntity('')}
            className="rounded border px-2 py-0.5 text-foreground"
          >
            {t('bacen:cases.filters.clear')}
          </button>
        </div>
      ) : null}

      {data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-lg border bg-card p-4" data-status-axes>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('bacen:cases.axes.title')}
            </h2>
            <Answer answer={data.status_axes} help={t('bacen:cases.axes.help')}>
              {(axes) => (
                <div className="space-y-3 text-sm">
                  <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {axes.appeal_kinds.map((kind) => (
                      <div
                        key={kind.code}
                        className="rounded-md border bg-muted/30 p-2"
                        data-axis={kind.code}
                      >
                        <dt className="text-xs text-muted-foreground">
                          {label('appealKind', kind.code)}
                        </dt>
                        <dd className="font-semibold tabular-nums text-foreground">
                          <Link
                            to={bacenCasesHref({ appeal_kind: kind.code })}
                            className="text-primary underline-offset-2 hover:underline"
                          >
                            {number.format(kind.count)}
                          </Link>
                          <span className="ml-1 text-xs font-normal text-muted-foreground">
                            ({percent.format(kind.share)})
                          </span>
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {axes.status_families.map((family) => (
                      <div
                        key={family.code}
                        className="rounded-md border bg-muted/30 p-2"
                        data-axis={family.code}
                      >
                        <dt className="text-xs text-muted-foreground">
                          {label('statusFamily', family.code)}
                        </dt>
                        <dd className="font-semibold tabular-nums text-foreground">
                          <Link
                            to={bacenCasesHref({ status_family: family.code })}
                            className="text-primary underline-offset-2 hover:underline"
                          >
                            {number.format(family.count)}
                          </Link>
                          <span className="ml-1 text-xs font-normal text-muted-foreground">
                            ({percent.format(family.share)})
                          </span>
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <p className="text-xs text-muted-foreground" data-still-owed>
                    {t('bacen:cases.axes.summary', {
                      persons: number.format(axes.persons),
                      companies: number.format(axes.companies),
                      stillOwed: number.format(axes.ruled_and_still_owed),
                    })}
                  </p>
                </div>
              )}
            </Answer>
            <div className="mt-3">
              <FieldGuide i18nKey="bacen:cases.guide.axes" />
            </div>
          </section>

          <section className="rounded-lg border bg-card p-4" data-concentration>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('bacen:cases.concentration.title')}
            </h2>
            <Answer answer={data.concentration} help={t('bacen:cases.concentration.help')}>
              {(c) => (
                <div className="space-y-2 text-sm">
                  <p className="text-xs text-muted-foreground">
                    {t('bacen:cases.concentration.summary', {
                      entities: number.format(c.entities),
                      rows: number.format(c.rows),
                      single: percent.format(c.single_row_share),
                      most: number.format(c.most_rows_for_one_entity),
                    })}
                  </p>
                  <ul className="grid grid-cols-5 gap-2">
                    {c.bands.map((band) => (
                      <li
                        key={band.label}
                        className="rounded-md border bg-muted/30 p-2"
                        data-band={band.label}
                      >
                        <div className="text-xs text-muted-foreground">
                          {t('bacen:cases.concentration.band', {
                            band: band.label,
                          })}
                        </div>
                        <div className="font-semibold tabular-nums text-foreground">
                          {number.format(band.entities)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Answer>
          </section>
        </div>
      ) : null}

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <EmptyState hint={t('bacen:cases.empty')} />
      ) : (
        <section className="rounded-lg border bg-card" data-stale={isPlaceholderData || undefined}>
          <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground">
            <span data-total={data?.total}>
              {t('bacen:cases.count', {
                loaded: number.format(items.length),
                total: number.format(data?.total ?? 0),
              })}
            </span>
          </div>
          <div ref={scrollRef} className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.pas')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.accused')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.office')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.first')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.board')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:cases.columns.firstFine')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:cases.columns.boardFine')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('bacen:cases.columns.status')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <CaseTableRow key={row.row_index} row={row} currency={currency} />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} />
          </div>
          {hasNextPage ? (
            <div className="border-t px-4 py-2 text-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={isFetchingNextPage}
                className="text-sm text-primary underline-offset-2 hover:underline"
              >
                {isFetchingNextPage ? t('common:state.loading') : t('bacen:cases.loadMore')}
              </button>
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}

export function CaseTableRow({
  row,
  currency,
}: {
  row: BacenCaseRow;
  currency: Intl.NumberFormat;
}) {
  const { t } = useTranslation(['bacen']);
  const label = useCodeLabel();
  return (
    <tr className="border-t align-top" data-row={row.row_index} data-pas={row.pas_number}>
      <td className="px-3 py-2">
        <Link
          to={`/bacen/c/${row.pas_number}`}
          className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
        >
          {row.pas_number}
        </Link>
        {row.duplicate_of !== null ? (
          <div className="text-xs text-muted-foreground">{t('bacen:cases.duplicate')}</div>
        ) : null}
      </td>
      <td className="px-3 py-2">
        <SubjectCell
          name={row.subject_name}
          subjectType={row.subject_type}
          maskedCpf={row.masked_cpf}
          cnpj={row.entity_cnpj}
        />
      </td>
      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
        {row.decision_office ?? '—'}
        {row.citation_date ? (
          <div className="font-sans tabular-nums">{row.citation_date}</div>
        ) : null}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {label('penalty', row.first_penalty_code)}
        {row.first_decision_date ? (
          <div className="text-xs tabular-nums">{row.first_decision_date}</div>
        ) : null}
        {row.first_term_years !== null ? (
          <div className="text-xs">{t('bacen:cases.years', { n: row.first_term_years })}</div>
        ) : null}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {row.ruled ? (
          <>
            {label('penalty', row.board_penalty_code)}
            <div className="text-xs tabular-nums">
              {row.board_decision_date ?? row.board_reference}
            </div>
            {row.appeal_kind === 'EX_OFFICIO' ? (
              <div className="text-xs">{label('appealKind', 'EX_OFFICIO')}</div>
            ) : null}
          </>
        ) : (
          <span className="italic">{label('appealKind', row.appeal_kind)}</span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {row.first_fine === null ? '—' : currency.format(row.first_fine)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-foreground">
        {row.board_fine === null ? '—' : currency.format(row.board_fine)}
        {row.trajectory && row.trajectory !== 'MAINTAINED' && row.trajectory !== 'NO_APPEAL' ? (
          <div className="text-xs text-muted-foreground">{label('trajectory', row.trajectory)}</div>
        ) : null}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {label('statusFamily', row.status_family)}
        <div className="text-xs">{row.status_raw}</div>
      </td>
    </tr>
  );
}
